// src/pages/Insights/InsightsPage.tsx
// Deep-dive rewrite: DeepDiveSection cards for Articles + Courses hubs — matching FeaturesPage design language

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { seoConfig } from '../../config/seo';
import {
  BookOpen, ArrowRight, Sparkles, GraduationCap, HelpCircle, Clock, CheckCircle, LucideIcon,
} from 'lucide-react';
import { m } from 'framer-motion';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Typography } from '../../components/ui/typography';

import { allPosts } from './Articles/ArticleData';
import { tutorials } from './Courses/CourseData';
import { Article, Course } from './types';

// ── Types ────────────────────────────────────────────────────────────────────

interface DeepDiveCard {
  id: string;
  icon: LucideIcon;
  title: string;
  tagline: string;
  description: string;
  useCases: string[];
  keyFacts: { label: string; value: string }[];
  link: string;
  cta: string;
  accentColor: string;
  accentBg: string;
}

// ── Hub Section Data ──────────────────────────────────────────────────────────

const hubCards: DeepDiveCard[] = [
  {
    id: 'articles',
    icon: BookOpen,
    title: 'Engineering Guides',
    tagline: 'Peer-reviewed technical depth, free forever',
    description:
      'In-depth long-form articles on the technologies powering FileFusion and modern software engineering at large. Topics include diff algorithm theory (Myers vs Patience), PDF internal architecture, OCR accuracy techniques, image compression mathematics, and security-conscious file handling. Written by practising engineers and updated as standards evolve.',
    useCases: [
      'Understand why your diff tool chose a specific change region',
      'Learn how PDF compression works before trusting a tool with sensitive documents',
      'Study OCR accuracy trade-offs before integrating it into a product',
      'Deep-dive into file format internals for an engineering interview',
    ],
    keyFacts: [
      { label: 'Depth', value: 'Long-form analysis' },
      { label: 'Authors', value: 'Practising engineers' },
      { label: 'Cost', value: 'Free, no paywall' },
    ],
    link: '/insights/articles',
    cta: 'Browse Articles',
    accentColor: '#1a73e8',
    accentBg: '#e8f0fe',
  },
  {
    id: 'courses',
    icon: GraduationCap,
    title: 'Programming Courses',
    tagline: 'Structured learning paths from zero to production',
    description:
      'Structured engineering courses covering Python, JavaScript, HTML5, and C. Each course progresses from core fundamentals to advanced topics — pointers and memory management in C, async/await patterns in JavaScript, DOM APIs in HTML5, and data science with Python. Code examples follow modern language standards and are tested against current runtime versions.',
    useCases: [
      'Master JavaScript async patterns before writing your first API client',
      'Understand C memory management before taking a systems programming role',
      'Learn Python from scratch to automate repetitive file tasks',
      'Get up to speed with HTML5 APIs for a frontend project quickly',
    ],
    keyFacts: [
      { label: 'Languages', value: 'Python · JS · HTML5 · C' },
      { label: 'Structure', value: 'Progressive paths' },
      { label: 'Code', value: 'Tested & current' },
    ],
    link: '/insights/courses',
    cta: 'Start Learning',
    accentColor: '#1e8e3e',
    accentBg: '#e6f4ea',
  },
];

// ── DeepDive Card Component ───────────────────────────────────────────────────

const DeepDiveSection: React.FC<{ card: DeepDiveCard; index: number }> = ({ card, index }) => {
  const Icon = card.icon;
  const flip = index % 2 !== 0;
  return (
    <m.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, delay: (index % 2) * 0.08 }}
      className="bg-white border border-[#e8eaed] rounded-[32px] overflow-hidden hover:shadow-lg transition-shadow duration-300"
    >
      <div className={`grid grid-cols-1 lg:grid-cols-2 ${flip ? 'lg:[&>div:first-child]:order-2' : ''}`}>
        {/* Left: content */}
        <div className="p-8 md:p-12 flex flex-col justify-between">
          <div>
            <div className="flex items-start gap-4 mb-6">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 mt-0.5"
                style={{ backgroundColor: card.accentBg, color: card.accentColor }}
              >
                <Icon size={24} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-2xl font-medium text-[#121317] leading-snug">{card.title}</h3>
                <p className="text-[0.9rem] mt-1" style={{ color: card.accentColor }}>{card.tagline}</p>
              </div>
            </div>
            <p className="text-[0.9375rem] text-[#5f6368] leading-[1.75] mb-8">{card.description}</p>
            <div className="flex flex-wrap gap-3 mb-8">
              {card.keyFacts.map(f => (
                <div key={f.label} className="flex flex-col px-4 py-2.5 rounded-2xl border border-[#e8eaed] bg-[#f8f9fa]">
                  <span className="text-[0.7rem] font-medium text-[#80868b] uppercase tracking-wide">{f.label}</span>
                  <span className="text-[0.9rem] font-medium text-[#121317]">{f.value}</span>
                </div>
              ))}
            </div>
            <Link
              to={card.link}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-white no-underline transition-all hover:opacity-90 hover:shadow-md text-[0.9375rem]"
              style={{ backgroundColor: card.accentColor }}
            >
              {card.cta} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        {/* Right: use-cases panel */}
        <div
          className="p-8 md:p-12 flex flex-col justify-center"
          style={{ backgroundColor: card.accentBg + '55' }}
        >
          <h4 className="text-[0.75rem] font-semibold uppercase tracking-widest mb-5" style={{ color: card.accentColor }}>
            Common use cases
          </h4>
          <ul className="space-y-4 list-none p-0 m-0">
            {card.useCases.map((uc, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle size={16} className="shrink-0 mt-0.5" style={{ color: card.accentColor }} />
                <span className="text-[0.9rem] text-[#3c4043] leading-snug">{uc}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </m.div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const InsightsPage: React.FC = () => {
  const featuredArticles: Article[] = [...allPosts]
    .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0))
    .slice(0, 3);

  const featuredCourses: Course[] = (tutorials as Course[]).slice(0, 3);

  return (
    <>
      <SEO {...seoConfig.insights} />

      <div className="min-h-screen bg-white font-brand text-[#202124]">
        <main>

          {/* ── Hero ────────────────────────────────────────────────────────── */}
          <header className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
            <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
              Learn. Build. <span className="text-[#1a73e8]">Ship.</span>
            </Typography>
            <Typography variant="hero-subtitle" weight="normal" className="max-w-2xl mx-auto text-[#5f6368] mb-10">
              Engineering knowledge for modern builders — peer-reviewed technical articles and
              structured programming courses, all free, no account required.
            </Typography>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/insights/courses"
                className="inline-flex items-center gap-2 px-7 py-3 bg-[#1a73e8] text-white font-medium rounded-full no-underline hover:bg-[#1557b0] transition-colors shadow-sm"
              >
                Start learning <ArrowRight size={17} />
              </Link>
              <Link
                to="/insights/articles"
                className="inline-flex items-center gap-2 px-7 py-3 bg-white border border-[#dadce0] text-[#3c4043] font-medium rounded-full no-underline hover:bg-[#f8f9fa] transition-colors"
              >
                Browse articles
              </Link>
            </div>
          </header>

          {/* ── Stat strip ──────────────────────────────────────────────────── */}
          <section className="max-w-7xl mx-auto px-6 pb-20 border-b border-[#f1f3f4]">
            <div className="flex justify-center gap-12 sm:gap-24 flex-wrap">
              {[
                { value: '2', label: 'Learning Tracks' },
                { value: '100%', label: 'Free Forever' },
                { value: '0', label: 'Account Needed' },
                { value: '∞', label: 'Always Updated' },
              ].map(s => (
                <div key={s.label} className="flex flex-col items-center text-center">
                  <div className="text-[2.75rem] font-light text-[#121317] leading-none mb-2">{s.value}</div>
                  <div className="text-[0.875rem] text-[#5f6368]">{s.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* ── Hub Deep-Dive Cards ──────────────────────────────────────────── */}
          <section className="max-w-7xl mx-auto px-6 py-20">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#e8f0fe] text-[#1a73e8]">
                    <Sparkles size={18} strokeWidth={1.5} />
                  </div>
                  <span className="text-[0.8rem] font-semibold uppercase tracking-widest text-[#1a73e8]">Insights Hub</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">Two tracks, one goal</h2>
                <p className="text-[#5f6368] text-lg leading-relaxed">
                  In-depth technical reading and structured programming paths — all free, built by engineers.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-6">
              {hubCards.map((card, i) => (
                <DeepDiveSection key={card.id} card={card} index={i} />
              ))}
            </div>
          </section>

          {/* ── Why Insights ────────────────────────────────────────────────── */}
          <section className="bg-white py-20 border-t border-[#f1f3f4]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="bg-[#f8f9fa] rounded-[32px] p-8 md:p-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div className="bg-white border border-[#e8eaed] rounded-2xl p-8 flex flex-col items-center">
                  <div className="w-14 h-14 bg-[#e8f0fe] rounded-2xl flex items-center justify-center mb-6">
                    <BookOpen size={28} className="text-[#1a73e8]" />
                  </div>
                  <h3 className="text-xl font-medium mb-3 text-[#121317]">In-Depth Articles</h3>
                  <p className="text-[#5f6368] leading-relaxed">
                    Peer-reviewed technical content written by practising engineers. Every article is research-backed, precise, and built to last.
                  </p>
                </div>
                <div className="bg-white border border-[#e8eaed] rounded-2xl p-8 flex flex-col items-center">
                  <div className="w-14 h-14 bg-[#e6f4ea] rounded-2xl flex items-center justify-center mb-6">
                    <GraduationCap size={28} className="text-[#1e8e3e]" />
                  </div>
                  <h3 className="text-xl font-medium mb-3 text-[#121317]">Structured Courses</h3>
                  <p className="text-[#5f6368] leading-relaxed">
                    Curated learning paths that progress from foundations to advanced topics, so you build genuine, transferable skills.
                  </p>
                </div>
                <div className="bg-white border border-[#e8eaed] rounded-2xl p-8 flex flex-col items-center">
                  <div className="w-14 h-14 bg-[#fef9e7] rounded-2xl flex items-center justify-center mb-6">
                    <Sparkles size={28} className="text-[#f9ab00]" />
                  </div>
                  <h3 className="text-xl font-medium mb-3 text-[#121317]">Free & Open</h3>
                  <p className="text-[#5f6368] leading-relaxed">
                    All content is permanently free — no paywalls, no sign-ups. Quality engineering education should be accessible to everyone.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ── Featured Courses ─────────────────────────────────────────────── */}
          <section className="max-w-7xl mx-auto px-6 py-24">
            <div className="flex flex-col md:flex-row justify-between items-baseline mb-12 gap-4">
              <div>
                <h2 className="text-3xl font-light text-[#121317] mb-3">Featured Courses</h2>
                <p className="text-[#5f6368] text-lg">Structured sequences designed for deep technical mastery</p>
              </div>
              <Link to="/insights/courses" className="text-[#1a73e8] font-medium flex items-center gap-1 hover:underline group">
                Browse all courses
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredCourses.map((c: Course) => (
                <Link
                  key={c.id}
                  to={`/insights/courses/${c.language}/${c.slug}`}
                  className="group flex flex-col bg-white border border-[#dadce0] rounded-[32px] overflow-hidden hover:shadow-md transition-all duration-300 no-underline"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#f8f9fa]">
                    <OptimizedImage src={c.imageUrl} alt={c.title} width={400} height={250} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" cinematic />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-[0.75rem] font-bold text-[#1e8e3e] uppercase tracking-wider mb-2">{c.language.toUpperCase()}</span>
                    <h3 className="text-xl font-medium text-[#121317] mb-3 line-clamp-2 min-h-[3.5rem] group-hover:text-[#1a73e8] transition-colors">{c.title}</h3>
                    <p className="text-[#5f6368] text-sm leading-relaxed mb-6 line-clamp-3">{c.excerpt}</p>
                    <div className="mt-auto flex items-center gap-4 pt-4 border-t border-[#f1f3f4] text-[#5f6368] text-sm">
                      <span className="flex items-center gap-1"><Clock size={14} />{c.readTime} min read</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* ── Latest Articles ──────────────────────────────────────────────── */}
          <section className="bg-white py-24 border-t border-[#f1f3f4]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-baseline mb-12 gap-4">
                <div>
                  <h2 className="text-3xl font-light text-[#121317] mb-3">Latest Articles</h2>
                  <p className="text-[#5f6368] text-lg">Strategic insights on technology, security, and digital productivity</p>
                </div>
                <Link to="/insights/articles" className="text-[#1a73e8] font-medium flex items-center gap-1 hover:underline group">
                  Browse all articles
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {featuredArticles.map((post: Article) => (
                  <Link
                    key={post.id}
                    to={`/insights/articles/${post.slug}`}
                    className="group flex flex-col bg-white border border-[#dadce0] rounded-[32px] overflow-hidden hover:shadow-md transition-all duration-300 no-underline"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-[#f8f9fa]">
                      <OptimizedImage src={post.imageUrl} alt={post.title} width={400} height={250} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" cinematic />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <span className="text-[0.75rem] font-bold text-[#1a73e8] uppercase tracking-wider mb-2">{post.category.replace(/-/g, ' ')}</span>
                      <h3 className="text-xl font-medium text-[#121317] mb-3 line-clamp-2 min-h-[3.5rem] group-hover:text-[#1a73e8] transition-colors">{post.title}</h3>
                      <p className="text-[#5f6368] text-sm leading-relaxed mb-6 line-clamp-3">{post.excerpt}</p>
                      <div className="mt-auto flex items-center gap-4 pt-4 border-t border-[#f1f3f4] text-[#5f6368] text-sm">
                        <span className="flex items-center gap-1"><Clock size={14} />{post.readTime} min read</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* ── FAQ / Help CTA ───────────────────────────────────────────────── */}
          <section className="bg-white py-24 border-t border-[#f1f3f4]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-12 bg-[#f8f9fa] rounded-[32px] p-8 md:p-16">
                <div className="max-w-xl text-center md:text-left">
                  <div className="w-12 h-12 bg-[#e8f0fe] rounded-2xl flex items-center justify-center text-[#1a73e8] mb-6 mx-auto md:mx-0">
                    <HelpCircle size={24} />
                  </div>
                  <h2 className="text-3xl font-light text-[#121317] mb-4">Questions about our content?</h2>
                  <p className="text-[#5f6368] text-lg mb-0 leading-relaxed">
                    Find information about course structure, content quality, update frequency, and how to get the most from our engineering library.
                  </p>
                </div>
                <div className="flex flex-col items-center gap-4 w-full md:w-auto shrink-0">
                  <Link to="/faq" className="w-fit flex items-center justify-center gap-3 px-6 py-2.5 bg-[#1a73e8] text-white rounded-full font-medium hover:bg-[#1557b0] transition-all shadow-sm no-underline">
                    Browse FAQs
                  </Link>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Link to="/insights/courses" className="text-sm font-medium text-[#5f6368] hover:text-[#121317] px-4 py-2 bg-white rounded-full border border-[#dadce0] transition-colors no-underline">All Courses</Link>
                    <Link to="/insights/articles" className="text-sm font-medium text-[#5f6368] hover:text-[#121317] px-4 py-2 bg-white rounded-full border border-[#dadce0] transition-colors no-underline">All Articles</Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </>
  );
};

export default InsightsPage;
